import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ArchiveButton } from "@/components/ArchiveDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, Flame, Wrench, Disc3, ChevronRight, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Share2 } from "lucide-react";
import { useServerView, activeFilters, ListActions, ListPrint, clamp2 } from "@/components/list/ListView";
import { QrFilterBar } from "@/components/quality/QrFilterBar";
import { PSummary } from "@/components/print/PrintDoc";
import { pl } from "@/components/quality/QrTools";
import { useProdLang } from "@/i18n/useProdLang";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const daDate = (s) => { const d = String(s || "").slice(0, 10); if (d.length === 10) { const [y, m, dd] = d.split("-"); return `${dd}-${m}-${y}`; } return "—"; };
const TYPE_ICON = { svejsning: Flame, montage: Wrench, slibning: Disc3 };
const STATUS = {
  Kladde: { bg: "#F3F1EC", tx: "#78716C", bd: "#E7E5E4", dot: "#A8A29E" },
  "I gang": { bg: "#F4EFEC", tx: "#806B61", bd: "#E6DDD6", dot: "#A08979" },
  "Færdig": { bg: "#E7EADF", tx: "#7E856C", bd: "#D3D8C6", dot: "#7E856C" },
  "Indført på stykliste": { bg: "#F1DEDA", tx: "#A83232", bd: "#E8C9C4", dot: "#A83232" },
};

const StatusBadge = ({ status, label }) => { const s = STATUS[status] || STATUS.Kladde; return (<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: s.bg, color: s.tx, border: `1px solid ${s.bd}` }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />{label || status}</span>); };

function TranslationToggle({ t }) {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/settings/translation").then((r) => setOn(!!r.data.enabled)).catch(() => setOn(true)); }, []);
  const toggle = () => { const v = !on; setOn(v); api.patch("/settings/translation", { enabled: v }).then((r) => setOn(!!r.data.enabled)).catch(() => setOn(!v)); };
  if (on === null) return null;
  return (
    <button type="button" data-testid="translation-toggle" onClick={toggle}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold [transition-property:background-color,color] duration-150 ${on ? "bg-[#7E856C] text-white border-[#7E856C]" : "bg-white text-stone-500 border-[#EBE8E3]"}`}
      title="Auto-oversættelse til/fra">
      {t("autotranslate_label")}: {on ? t("on") : t("off")}
    </button>
  );
}

export default function Documents({ lockedType }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const sv = useServerView(`/doc-view/list${lockedType ? `?locked_type=${lockedType}` : ""}`, { exportPath: `/doc-view/list/export.xlsx${lockedType ? `?locked_type=${lockedType}` : ""}` });
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [draft, setDraft] = useState({ template_key: "svejsning" });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canEdit = user?.role === "admin" || (user?.departments || []).includes("produktion") || (user?.departments || []).includes("salg");
  const { lang, setLang, t, dir } = useProdLang();

  const load = sv.reload;
  const loadSaved = () => api.get("/saved-filters?scope=documents").then((r) => setSavedFilters(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const statusSel = sv.getArr("status");
  const typeSel = sv.getArr("type");
  const varenrText = sv.params.get("q") || "";
  const visning = params.get("visning");
  const setParam = sv.setParam;
  const resetFilters = sv.reset;
  const applySaved = (sf) => { const q = new URLSearchParams(sf.query || ""); if (q.get("varenr") && !q.get("q")) { q.set("q", q.get("varenr")); q.delete("varenr"); } setParams(q); };
  const saveCurrent = async () => {
    if (!saveName.trim()) return;
    try { await api.post("/saved-filters", { name: saveName.trim(), query: params.toString(), scope: "documents" }); toast.success(t("toast_filter_saved")); setSaveOpen(false); setSaveName(""); loadSaved(); }
    catch (e) { toast.error(t("toast_filter_save_err")); }
  };
  const deleteSaved = async (id, e) => { e?.stopPropagation?.(); try { await api.delete(`/saved-filters/${id}`); toast.success(t("toast_filter_deleted")); loadSaved(); } catch (_) {} };

  const reports = sv.data === null ? null : sv.rows;
  const filtered = sv.rows;
  const total = sv.data?.total ?? 0;
  const tr = (f, pre) => ({ ...f, options: f.options.map((o) => ({ ...o, label: t(`${pre}${o.value}`) })) });
  const barFilters = [...(lockedType ? [] : [tr(sv.mkFilter("type", t("type")), "type_")]), tr(sv.mkFilter("status", t("status_label")), "status_"), sv.mkFilter("ansvarlig", t("col_responsible"))];
  const pfilters = activeFilters(sv, { type: t("type"), status: t("status_label"), ansvarlig: t("col_responsible") }).map(([k, v]) => k === t("type") ? [k, v.split(", ").map((x) => t(`type_${x}`)).join(", ")] : k === t("status_label") ? [k, v.split(", ").map((x) => t(`status_${x}`)).join(", ")] : [k, v]);
  if (visning === "mit-ansvar") pfilters.unshift([t("quick_view"), "Mit ansvar"]);

  const sortKey = params.get("sort") || "";
  const sortDir = params.get("dir") || "asc";
  const toggleSort = (col) => setParam(sortKey === col ? { sort: col, dir: sortDir === "asc" ? "desc" : "asc" } : { sort: col, dir: "asc" });
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const val = (r) => ({ name: (r.title || "").toLowerCase(), ansvarlig: (r.responsible_name || "").toLowerCase(), status: (r.status || "").toLowerCase(), version: r.revision ?? 0, updated: r.updated_at || "" }[sortKey] ?? "");
    const arr = [...filtered].sort((a, b) => { const x = val(a), y = val(b); return x < y ? -1 : x > y ? 1 : 0; });
    return sortDir === "desc" ? arr.reverse() : arr;
  }, [filtered, sortKey, sortDir]);
  const SortIcon = ({ col }) => sortKey !== col ? <ArrowUpDown className="h-3.5 w-3.5 opacity-40" /> : sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-primary" /> : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
  const SortH = ({ col, span, children }) => (<button onClick={() => toggleSort(col)} data-testid={`doc-sort-${col}`} className={`${span} flex items-center gap-1.5 label-caps hover:text-stone-700 [transition-property:color] duration-200`}>{children} <SortIcon col={col} /></button>);

  const openShare = async (r, e) => {
    e?.stopPropagation?.();
    let token = r.share_token;
    if (!token) {
      try {
        const { data } = await api.post(`/quality-reports/${r.id}/share`);
        token = data.share_token;
        sv.reload();
      } catch (err) { toast.error(t("toast_share_err")); return; }
    }
    window.open(`${window.location.origin}/kvalitetsrapport-kunde/${token}`, "_blank");
  };

  const doDeleteReport = async () => {
    try { await api.delete(`/quality-reports/${deleteTarget.id}`); sv.reload(); toast.success(t("toast_doc_deleted")); setDeleteTarget(null); }
    catch (e) { toast.error(e.response?.data?.detail || t("toast_doc_delete_err")); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const qp = new URLSearchParams({ module: "dokumenter" });
      const types = lockedType ? [lockedType] : typeSel;
      if (types.length) qp.set("type", types.join(","));
      if (statusSel.length) qp.set("status", statusSel.join(","));
      if (varenrText.trim()) qp.set("q", varenrText.trim());
      if (visning === "mit-ansvar") qp.set("visning", "mit-ansvar");
      const res = await api.get(`/quality-reports/export/excel?${qp.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `dokumenter-${lockedType || "alle"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("toast_export_err")); }
    finally { setExporting(false); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const tk = lockedType || draft.template_key;
      const { data } = await api.post("/quality-reports", { type: tk, template_key: tk });
      toast.success(t("toast_doc_created")); nav(`/dokument/${data.id}`);
    } catch (e) { toast.error(t("toast_doc_create_err")); } finally { setCreating(false); setOpen(false); }
  };

  return (
    <div className="rise-in" data-testid="doc-page" dir={dir}>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <p className="label-caps mb-2">{t("prod")}</p>
          <h1 className={`text-4xl sm:text-5xl font-light text-[#1A1919] ${dir === "rtl" ? "" : "tracking-tight"}`}>{lockedType ? t(`dept_${lockedType}`) : t("page_documents")}</h1>
          <p className="text-stone-500 mt-2 text-sm">{lockedType ? t(`desc_${lockedType}`) : t("desc_default")}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 shrink-0 md:max-w-[62%]" dir="ltr">
          <LanguageSwitcher lang={lang} setLang={setLang} />
          {user?.role === "admin" && <TranslationToggle t={t} />}
          <ListActions t={t} onExport={canEdit ? sv.exportXlsx : undefined} more={[]}
            primary={canEdit && <Button data-testid="doc-create-btn" onClick={() => (lockedType ? create() : setOpen(true))} disabled={creating} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a] shrink-0"><Plus className="h-4 w-4 mr-1.5" /> {creating && lockedType ? t("creating") : t("create_document")}</Button>}>
            {user?.role === "admin" && <ArchiveButton kind="quality_reports" label={t("fb_archived") || "Arkiverede"} onRestored={load} />}</ListActions>
        </div>
      </div>

      <QrFilterBar t={t} search={sv.search} setSearch={sv.onSearch} hideQuick placeholder={lockedType ? t("search_placeholder") : t("title_or_item")} shown={filtered.length} total={total} onReset={resetFilters} leadActive={visning === "mit-ansvar"} match={sv.match} setMatch={sv.setMatch}
        filters={barFilters} period={sv.period} saved={savedFilters} onApplySaved={applySaved} onDeleteSaved={deleteSaved} onSaveCurrent={() => setSaveOpen(true)}
        lead={!lockedType && <div className="inline-flex rounded-xl border border-[#EBE8E3] bg-white p-0.5 h-9 items-center flex-wrap" data-testid="doc-quick-view">
          <button type="button" data-testid="doc-quick-all" onClick={() => setParam({ visning: null, type: [] })} className={`px-3.5 h-full rounded-[10px] text-sm font-medium ${!visning && typeSel.length === 0 ? "bg-[#A83232] text-white" : "text-stone-600 hover:text-stone-900"}`}>{t("all_reports")}</button>
          {["montage", "svejsning", "slibning"].map((v) => (
            <button key={v} type="button" data-testid={`doc-quick-${v}`} onClick={() => setParam({ type: [v] })} className={`px-3.5 h-full rounded-[10px] text-sm font-medium ${typeSel.length === 1 && typeSel[0] === v ? "bg-[#A83232] text-white" : "text-stone-600 hover:text-stone-900"}`}>{t(`type_${v}`)}</button>
          ))}
        </div>} />
      <ListPrint kicker="BSM A/S · Produktion" title={lockedType ? t(`dept_${lockedType}`) : "Dokumenter"} noun="dokumenter" filters={pfilters}
        kpis={[{ label: "Dokumenter i udvalget", value: filtered.length }, { label: "Kladder", value: filtered.filter((r) => r.status === "Kladde").length }, { label: "I gang", value: filtered.filter((r) => r.status === "I gang").length, dark: true }, { label: "Færdige", value: filtered.filter((r) => r.status === "Færdig" || r.status === "Indført på stykliste").length }]}
        summary={<PSummary>{pl(filtered.length, "dokument", "dokumenter")} i udvalget ud af {total} i alt.</PSummary>}
        cols={[{ label: "Dok.nr.", key: "doc_no", w: "22mm", cls: "nowrap" }, { label: "Type", key: "ty", w: "20mm", cls: "nowrap" }, { label: "Titel", key: "t2" }, { label: "Varenr.", key: "item_no", w: "24mm", cls: "nowrap" }, { label: "Ansvarlig", key: "r2", w: "30mm" }, { label: "Status", key: "status", w: "30mm" }, { label: "Ver.", key: "ver", w: "12mm", cls: "nowrap" }, { label: "Opdateret", key: "upd", w: "22mm", cls: "nowrap" }]}
        rows={sorted.map((r) => ({ ...r, ty: t(`type_${r.type}`), t2: clamp2(r.title || t("untitled")), r2: clamp2(r.responsible_name || "—"), ver: r.revision || 1, upd: daDate(r.updated_at) }))} />

      {reports === null ? (
        <div className="text-stone-400 text-sm py-16 text-center">{t("loading")}</div>
      ) : total === 0 ? (
        <div className="pcard p-14 text-center" data-testid="doc-empty">
          <div className="h-14 w-14 rounded-2xl bg-[#F3F1EC] flex items-center justify-center mx-auto mb-4"><FileText className="h-6 w-6 text-stone-400" /></div>
          <p className="text-stone-600 font-medium">{t("empty_title")}</p>
          <p className="text-stone-400 text-sm mt-1">{t("empty_desc")}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="pcard p-14 text-center" data-testid="doc-no-match">
          <p className="text-stone-600 font-medium">{t("no_match")}</p>
          <Button variant="outline" className="mt-5 rounded-xl border-[#EBE8E3]" onClick={resetFilters}>{t("reset_filters")}</Button>
        </div>
      ) : (
        <div className="pcard overflow-hidden" data-testid="doc-list">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#FBFAF8] border-b border-[#EBE8E3]">
            <SortH col="name" span="col-span-4">{t("col_document")}</SortH>
            <SortH col="ansvarlig" span="col-span-3">{t("col_responsible")}</SortH>
            <SortH col="status" span="col-span-2">{t("col_status")}</SortH>
            <SortH col="version" span="col-span-1">{t("col_version")}</SortH>
            <SortH col="updated" span="col-span-1">{t("col_updated")}</SortH>
            <div className="col-span-1 label-caps text-right">{t("col_action")}</div>
          </div>
          <div className="divide-y divide-[#F0EDE8]">
          {sorted.map((r) => {
            const TIcon = TYPE_ICON[r.type] || FileText;
            const sub = r.item_no ? `${t("varenr")} ${r.item_no}` : "";
            return (
              <div key={r.id} data-testid={`doc-row-${r.id}`} className="hover:bg-[#FBFAF8] [transition-property:background-color] duration-150">
                <div data-testid={`doc-row-body-${r.id}`} onClick={() => nav(`/dokument/${r.id}`)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); nav(`/dokument/${r.id}`); } }} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 md:items-center p-4 md:px-5 md:py-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A83232]">
                  <div className="md:col-span-4 min-w-0">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-stone-400 uppercase tracking-wide"><TIcon className="h-3.5 w-3.5 text-[#A83232]" /> {t(`type_${r.type}`)}{r.doc_no && <span className="ml-1.5 font-mono text-stone-500 normal-case tracking-normal">· {r.doc_no}</span>}</span>
                    <p className="font-semibold text-[#1A1919] truncate mt-0.5">{r.title || t("untitled")}</p>
                    {sub && <p className="text-xs text-muted-foreground truncate mt-0.5">{sub}</p>}
                  </div>
                  <div className="md:col-span-3 text-sm text-stone-600 truncate"><span className="md:hidden label-caps mr-2">{t("col_responsible")}</span>{r.responsible_name || "—"}</div>
                  <div className="md:col-span-2"><span className="md:hidden label-caps mr-2">{t("col_status")}</span><StatusBadge status={r.status} label={t(`status_${r.status}`)} /></div>
                  <div className="md:col-span-1 text-sm text-stone-600"><span className="md:hidden label-caps mr-2">{t("col_version")}</span>{r.revision || 1}</div>
                  <div className="md:col-span-1 text-sm text-stone-600"><span className="md:hidden label-caps mr-2">{t("col_updated")}</span>{daDate(r.updated_at)}</div>
                  <div className="md:col-span-1 flex items-center gap-1 md:justify-end" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`doc-share-${r.id}`} onClick={(e) => openShare(r, e)} title={t("share")} className="p-1.5 rounded-lg text-muted-foreground hover:text-[#A83232] hover:bg-[#F1DEDA] [transition-property:color,background-color] duration-200"><Share2 className="h-4 w-4" strokeWidth={1.75} /></button>
                    {canEdit && <button data-testid={`doc-delete-${r.id}`} onClick={() => setDeleteTarget(r)} title={t("delete_document")} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-[#F1DEDA] [transition-property:color,background-color] duration-200"><Trash2 className="h-4 w-4" strokeWidth={1.75} /></button>}
                    <ChevronRight className="h-4 w-4 text-stone-300 hidden md:block" />
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-2xl" dir={dir}>
          <DialogHeader><DialogTitle className="text-xl font-medium">{t("save_filter")}</DialogTitle><DialogDescription>{t("save_filter_desc")}</DialogDescription></DialogHeader>
          <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder={t("filter_name_ph")} data-testid="doc-save-filter-name" className="rounded-xl" onKeyDown={(e) => { if (e.key === "Enter") saveCurrent(); }} />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setSaveOpen(false)}>{t("cancel")}</Button>
            <Button className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={saveCurrent} data-testid="doc-save-filter-confirm">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="doc-create-dialog" dir={dir}>
          <DialogHeader><DialogTitle>{t("new_document")}</DialogTitle><DialogDescription>{t("new_document_desc")}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-1">
            {[{ key: "svejsning", icon: Flame }, { key: "montage", icon: Wrench }, { key: "slibning", icon: Disc3 }].map((tpl) => (
              <button key={tpl.key} data-testid={`doc-template-${tpl.key}`} onClick={() => setDraft({ template_key: tpl.key })} className={`p-4 rounded-xl border text-left transition-colors ${draft.template_key === tpl.key ? "border-[#A83232] bg-[#F1DEDA]" : "border-[#EBE8E3] hover:bg-[#F3F1EC]"}`}>
                <tpl.icon className="h-5 w-5 text-[#A83232] mb-2" />
                <p className="font-semibold text-sm text-[#1A1919]">{t(`type_${tpl.key}`)}</p>
                <p className="text-xs text-stone-400">{t(`tpl_${tpl.key}_desc`)}</p>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">{t("cancel")}</Button>
            <Button data-testid="doc-create-confirm" onClick={create} disabled={creating} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{creating ? t("creating") : t("create_and_edit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="rounded-2xl" dir={dir}>
          <DialogHeader><DialogTitle className="text-xl font-medium">{t("delete_document")}</DialogTitle><DialogDescription>{t("delete_confirm_pre")}"{deleteTarget?.title || t("untitled")}"{t("delete_confirm_post")}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDeleteTarget(null)}>{t("cancel")}</Button>
            <Button className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={doDeleteReport} data-testid="doc-confirm-delete">{t("delete_document")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
